/**
 * Kronos-style panels for the Roster module:
 *   • TemplatesPanel  — PM defines recurring weekly room slots + one-click
 *                       "Generate open shifts for a week".
 *   • CoveragePanel   — Rooms × Days grid showing staffed / open / gap
 *                       status at a glance.
 *
 * Wires against /api/roster-kronos/* (routes/roster_kronos.py).
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Plus, Trash2, Loader2, PlayCircle, Users, MapPin, AlertTriangle } from 'lucide-react';
import { API, mondayOf, addDays, isoDate } from './_helpers';

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const PURPLE = '#7C3AED';


// ============================================================================
// TemplatesPanel
// ============================================================================
export function TemplatesPanel({ isManager }) {
  const [templates, setTemplates] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const reload = async () => {
    setLoading(true);
    const [t, s] = await Promise.all([
      axios.get(`${API}/roster-kronos/templates`, { withCredentials: true }),
      axios.get(`${API}/roster/sites`, { withCredentials: true }),
    ]);
    setTemplates(t.data.templates || []);
    setSites(s.data.sites || []);
    setLoading(false);
  };
  useEffect(() => { reload(); }, []);

  const grouped = useMemo(() => {
    const g = {};
    (templates || []).forEach(t => {
      g[t.site_id] = g[t.site_id] || { label: t.site_label, rows: [] };
      g[t.site_id].rows.push(t);
    });
    return g;
  }, [templates]);

  const removeT = async (tid) => {
    if (!window.confirm('Delete this template slot?')) return;
    await axios.delete(`${API}/roster-kronos/templates/${tid}`, { withCredentials: true });
    reload();
  };

  const generateWeek = async () => {
    setBusy(true); setMsg('');
    try {
      const monday = isoDate(mondayOf(new Date()));
      const r = await axios.post(`${API}/roster-kronos/generate-open-shifts?week_start=${monday}`,
        {}, { withCredentials: true });
      setMsg(`Created ${r.data.created} open shifts (skipped ${r.data.skipped} already existing). Week of ${r.data.week_start}. Assign them from the Roster tab.`);
    } catch (e) {
      setMsg(e?.response?.data?.detail || 'Failed to generate.');
    }
    setBusy(false);
  };

  if (loading) return <div className="py-10 text-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-4" data-testid="templates-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold" style={{ color: '#0d2644' }}>Room templates</h2>
          <p className="text-sm text-muted-foreground">
            Define recurring weekly slots per room. One click generates open shifts for the current week — you then assign staff.
          </p>
        </div>
        {isManager && (
          <div className="flex items-center gap-2">
            <button onClick={generateWeek} disabled={busy || templates.length === 0}
              data-testid="generate-week-btn"
              className="text-sm font-semibold inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-white disabled:opacity-40"
              style={{ background: PURPLE }}>
              <PlayCircle className="w-4 h-4" /> Generate open shifts for this week
            </button>
            <button onClick={() => setShowForm(true)} data-testid="new-template-btn"
              className="text-sm font-semibold inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border hover:bg-muted/40">
              <Plus className="w-4 h-4" /> Add slot
            </button>
          </div>
        )}
      </div>

      {msg && <div className="rounded-lg border border-purple-200 bg-purple-50 p-3 text-sm" data-testid="templates-msg">{msg}</div>}

      {templates.length === 0 ? (
        <div className="text-sm text-muted-foreground rounded-lg border border-dashed p-6 text-center">
          No templates yet. Add a slot to define recurring room coverage (e.g. "Ultrasound Room 1 · Mon 08:00–16:00").
        </div>
      ) : (
        Object.entries(grouped).map(([sid, g]) => (
          <div key={sid} className="rounded-xl border bg-card overflow-hidden">
            <div className="px-4 py-2 bg-muted/30 border-b flex items-center gap-2">
              <MapPin className="w-4 h-4 text-muted-foreground" />
              <span className="font-semibold text-sm" style={{ color: '#0d2644' }}>{g.label || sid}</span>
              <span className="text-xs text-muted-foreground">· {g.rows.length} slot{g.rows.length===1?'':'s'}</span>
            </div>
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-muted-foreground">
                <tr className="border-b"><th className="px-4 py-2 text-left">Day</th><th className="px-4 py-2 text-left">Start</th><th className="px-4 py-2 text-left">End</th><th className="px-4 py-2 text-left">Role</th><th className="px-4 py-2 text-left">Break (min)</th><th /></tr>
              </thead>
              <tbody>
                {g.rows.sort((a,b) => a.day_of_week - b.day_of_week || a.start.localeCompare(b.start)).map(t => (
                  <tr key={t.template_id} className="border-t border-border">
                    <td className="px-4 py-2 font-medium">{DAY_LABELS[t.day_of_week]}</td>
                    <td className="px-4 py-2">{t.start}</td>
                    <td className="px-4 py-2">{t.end}</td>
                    <td className="px-4 py-2 text-muted-foreground">{t.role_label || '—'}</td>
                    <td className="px-4 py-2 text-muted-foreground">{t.break_min}</td>
                    <td className="px-4 py-2 text-right">
                      {isManager && (
                        <button onClick={() => removeT(t.template_id)} data-testid={`del-template-${t.template_id}`}
                          className="text-red-600 hover:text-red-700 p-1"><Trash2 className="w-4 h-4" /></button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))
      )}

      {showForm && <NewTemplateForm sites={sites} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); reload(); }} />}
    </div>
  );
}


function NewTemplateForm({ sites, onClose, onSaved }) {
  const [siteId, setSiteId] = useState(sites[0]?.site_id || '');
  const [dow, setDow] = useState(0);
  const [start, setStart] = useState('08:00');
  const [end, setEnd] = useState('16:00');
  const [role, setRole] = useState('');
  const [breakMin, setBreakMin] = useState(30);
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setErr(''); setSaving(true);
    try {
      await axios.post(`${API}/roster-kronos/templates`, {
        site_id: siteId, day_of_week: dow, start, end, role_label: role, break_min: breakMin,
      }, { withCredentials: true });
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to save.');
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card rounded-xl shadow-2xl w-full max-w-md p-5 space-y-3" onClick={e => e.stopPropagation()} data-testid="new-template-form">
        <h3 className="text-lg font-semibold" style={{ color: '#0d2644' }}>New room-template slot</h3>
        <label className="block text-sm">
          <span className="text-xs font-medium text-muted-foreground">Room</span>
          <select value={siteId} onChange={e => setSiteId(e.target.value)} data-testid="tpl-site"
            className="w-full mt-0.5 px-3 py-2 rounded-lg border bg-background text-sm">
            {sites.map(s => <option key={s.site_id} value={s.site_id}>{s.label}</option>)}
          </select>
        </label>
        <label className="block text-sm">
          <span className="text-xs font-medium text-muted-foreground">Day of week</span>
          <select value={dow} onChange={e => setDow(Number(e.target.value))} data-testid="tpl-dow"
            className="w-full mt-0.5 px-3 py-2 rounded-lg border bg-background text-sm">
            {DAY_LABELS.map((d, i) => <option key={i} value={i}>{d}</option>)}
          </select>
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-sm">
            <span className="text-xs font-medium text-muted-foreground">Start</span>
            <input type="time" value={start} onChange={e => setStart(e.target.value)} data-testid="tpl-start"
              className="w-full mt-0.5 px-3 py-2 rounded-lg border bg-background text-sm" />
          </label>
          <label className="block text-sm">
            <span className="text-xs font-medium text-muted-foreground">End</span>
            <input type="time" value={end} onChange={e => setEnd(e.target.value)} data-testid="tpl-end"
              className="w-full mt-0.5 px-3 py-2 rounded-lg border bg-background text-sm" />
          </label>
        </div>
        <label className="block text-sm">
          <span className="text-xs font-medium text-muted-foreground">Role label (optional)</span>
          <input type="text" value={role} onChange={e => setRole(e.target.value)} data-testid="tpl-role"
            placeholder="e.g. Sonographer" className="w-full mt-0.5 px-3 py-2 rounded-lg border bg-background text-sm" />
        </label>
        <label className="block text-sm">
          <span className="text-xs font-medium text-muted-foreground">Break (min)</span>
          <input type="number" value={breakMin} onChange={e => setBreakMin(Number(e.target.value)||0)} data-testid="tpl-break"
            className="w-full mt-0.5 px-3 py-2 rounded-lg border bg-background text-sm" />
        </label>
        {err && <p className="text-sm text-red-600">{err}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="text-sm px-3 py-2 rounded-lg border">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="tpl-save"
            className="text-sm font-semibold px-3 py-2 rounded-lg text-white disabled:opacity-40"
            style={{ background: PURPLE }}>{saving ? 'Saving…' : 'Save slot'}</button>
        </div>
      </div>
    </div>
  );
}


// ============================================================================
// CoveragePanel — Rooms × Days grid
// ============================================================================
export function CoveragePanel() {
  const [weekStart, setWeekStart] = useState(mondayOf(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const from_ = isoDate(weekStart);
    const to_ = isoDate(addDays(weekStart, 6));
    setLoading(true);
    axios.get(`${API}/roster-kronos/coverage?from=${from_}&to=${to_}`, { withCredentials: true })
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, [weekStart]);

  const prev = () => setWeekStart(addDays(weekStart, -7));
  const next = () => setWeekStart(addDays(weekStart, 7));
  const today = () => setWeekStart(mondayOf(new Date()));

  if (loading || !data) return <div className="py-10 text-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-3" data-testid="coverage-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold" style={{ color: '#0d2644' }}>Coverage — {data.days[0]} → {data.days[6]}</h2>
          <p className="text-sm text-muted-foreground">Rooms × days. Green = staffed. Amber = open shift (unassigned). Red = template slot with no coverage.</p>
        </div>
        <div className="inline-flex rounded-lg border overflow-hidden">
          <button onClick={prev} className="px-3 py-1.5 text-sm hover:bg-muted/40">←</button>
          <button onClick={today} className="px-3 py-1.5 text-sm border-l hover:bg-muted/40">Today</button>
          <button onClick={next} className="px-3 py-1.5 text-sm border-l hover:bg-muted/40">→</button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border bg-card">
        <table className="w-full text-xs">
          <thead className="text-[10px] uppercase tracking-wider text-muted-foreground">
            <tr className="border-b">
              <th className="px-3 py-2 text-left" style={{ minWidth: 160 }}>Room</th>
              {data.days.map((d, i) => (
                <th key={d} className="px-2 py-2 text-left" style={{ minWidth: 130 }}>
                  <div>{data.day_names[i]}</div>
                  <div className="text-muted-foreground normal-case tracking-normal">{d.slice(5)}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.entries(data.grid).map(([sid, room]) => (
              <tr key={sid} className="border-t border-border align-top">
                <td className="px-3 py-2 font-semibold whitespace-nowrap" style={{ color: '#0d2644' }}>
                  <MapPin className="w-3.5 h-3.5 inline mr-1 text-muted-foreground" />
                  {room.label || sid}
                </td>
                {data.days.map(d => <CoverageCell key={d} cell={room.days[d]} />)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function CoverageCell({ cell }) {
  const templates = cell?.template_slots || [];
  const staffed = cell?.staffed || [];
  const open = cell?.open || [];
  // A gap = template slot with no matching staffed or open shift covering the same time.
  const isGap = templates.length > 0 && staffed.length === 0 && open.length === 0;
  const bg = isGap ? 'rgba(220,38,38,0.10)'
    : (staffed.length > 0 && open.length === 0) ? 'rgba(34,197,94,0.10)'
    : (open.length > 0) ? 'rgba(234,179,8,0.14)'
    : 'transparent';
  const border = isGap ? '#FCA5A5'
    : (staffed.length > 0 && open.length === 0) ? '#86EFAC'
    : (open.length > 0) ? '#FDE68A'
    : 'transparent';
  return (
    <td className="px-2 py-1.5" style={{ background: bg, borderTop: `1px solid ${border}` }}>
      {isGap && (
        <div className="flex items-center gap-1 text-[11px] font-semibold text-red-700">
          <AlertTriangle className="w-3 h-3" /> Gap
        </div>
      )}
      {staffed.map((s, i) => (
        <div key={`s-${i}`} className="text-[11px] font-semibold text-emerald-800 truncate">
          <Users className="w-3 h-3 inline mr-1" />
          {s.user_name} · {s.start}–{s.end}
        </div>
      ))}
      {open.map((o, i) => (
        <div key={`o-${i}`} className="text-[11px] font-semibold text-amber-800 truncate">
          Open · {o.start}–{o.end}
        </div>
      ))}
      {templates.length > 0 && staffed.length === 0 && open.length === 0 && (
        <div className="text-[10px] text-red-700 truncate">
          {templates.map(t => `${t.start}–${t.end}`).join(', ')}
        </div>
      )}
    </td>
  );
}
